import { AriApiQueryCreatorFetch } from './AriApiCreator';
import { prismScopes } from './auth';
import { ChipsHarvestYield } from './AIMApi';
import getPrismBaseUrl from './AIMApi/controllers/utils/getBaseUrl';

// ─── Response types for GetChipsYieldSummary ────────────────────────────────

/**
 * A single row of the Chips yield summary response, containing division, crop, yield, acreage, and financial data fields.
 */
export interface ChipsYieldSummaryRow {
  division: string;
  cropYear: number;
  varietyShort: string;
  varietyDesc: string | null;
  blockID: string | null;
  farmField: string | null;
  areaName: string | null;
  cropStatus: string | null;
  crop: string | null;           // Enterprise name (e.g., "Almonds", "Walnuts")
  goodMeatWt: number | null;
  harvestAcres: number | null;
  yieldPerAcre: number | null;
  plantAcres: number | null;
  plantYear: number | null;
}

/**
 * The full response shape for the Chips yield summary API, combining current crop year mappings with an array of yield data rows.
 */
export interface ChipsYieldSummaryResponse {
  currentCropYears: Record<string, number>;
  yields: ChipsYieldSummaryRow[];
}

// ─── RTK Query Service ──────────────────────────────────────────────────────

/**
 * RTK Query API service for the Chips (ChipsHarvestYield) data endpoints, providing farm field lookup and harvest yield queries.
 *
 * Built with AriApiQueryCreatorFetch using the Prism scopes; exported hooks are re-exported from this module's index.
 */
export const chipsService = AriApiQueryCreatorFetch({
  serviceURL: getPrismBaseUrl(),
  scopesObj: prismScopes,
  apiOptions: {
    reducerPath: 'chipsApi',
    endpoints: (builder) => ({
      chipsFarmField: builder.query<any[], { businessUnitCode: string; cropYear: string }>({
        query: ({ businessUnitCode, cropYear }) => ({
          url: '/ChipsFarmField',
          params: { CropYear: cropYear, BusinessUnitCode: businessUnitCode }
        })
      }),
      chipsHarvestYield: builder.query<ChipsHarvestYield[], { businessUnitCode: string }>({
        query: ({ businessUnitCode }) => ({
          url: '/GetChipsHarvestYield',
          params: { BusinessUnitCode: businessUnitCode }
        })
      }),
      chipsHarvestCropsYield: builder.query<any[], string[]>({
        query: (data) => ({
          url: '/GetChipsHarvestCropYields',
          method: 'POST',
          body: data
        })
      }),
      /** Returns all crop years of yield data for SVF/DFC divisions + resolved current crop years */
      chipsYieldSummary: builder.query<ChipsYieldSummaryResponse, { divisions: string[] }>({
        query: ({ divisions }) => ({
          url: '/GetChipsYieldSummary',
          method: 'POST',
          body: { divisions }
        })
      })
    })
  }
});

export const {
  useLazyChipsFarmFieldQuery,
  useLazyChipsHarvestCropsYieldQuery,
  useLazyChipsHarvestYieldQuery,
  useChipsYieldSummaryQuery
} = chipsService.hooks;
